// src/components/FooterActionBar.jsx
"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { House, Plus, CircleDot, X } from "lucide-react";
import DiscussionDrawer from "@/components/panels/DiscussionDrawer";
import CreateDiscussionComposer from "@/components/panels/CreateDiscussionComposer";
import { auth, db } from "@/firebase/config";
import { collection, query, where, limit as qLimit, getDocs } from "@/lib/firestore-instrumented";
import { buildLoginRedirect } from "@/lib/authRedirect";
import { usePortal } from "@/context/PortalContext";

export default function FooterActionBar() {
  const portal = usePortal();
  const pathname = usePathname();
  const router = useRouter();
  const search = useSearchParams();
  const [open, setOpen] = useState(false);       // composer drawer
  const [gateOpen, setGateOpen] = useState(false); // membership gate drawer
  const [checking, setChecking] = useState(false);

  // Tabs on the Home page (CardFeedClient): t=latest | hubs | ...
  const t = (search.get("t") || search.get("tab") || "").toLowerCase();

  const isHomeTab = pathname === "/" && (t === "" || t === "latest");
  const isMyHubsTab = pathname === "/" && t === "myhubs";

  // If we’re inside a hub page, capture slug from path `/h/[slug]`
  const inHub = pathname?.startsWith("/h/");
  const hubSlug = inHub ? pathname.split("/")[2] : null;

  // Colors
  const baseClr = "text-[var(--muted)]";
  const activeClr = "text-[var(--brand)]";

  const navigateHomeTab = (tab) => {
    router.push(`/?t=${tab}`);
  };

  const handlePostClick = async () => {
    // If already checking, debounce
    if (checking) return;
    setChecking(true);
    try {
      const u = auth.currentUser;
      if (!u?.uid) {
        window.location.assign(buildLoginRedirect({ intent: "create-post" }));
        return;
      }
      // Does the user belong to any hubs? (active membership)
      const memSnap = await getDocs(
        query(
          collection(db, "hubMembers"),
          where("uid", "==", u.uid),
          where("status", "==", "active"),
          qLimit(portal ? 250 : 1)
        )
      );
      const hasEligibleMembership = portal
        ? memSnap.docs.some((snap) => portal.allowedHubSlugSet?.has(snap.data()?.hubId))
        : !memSnap.empty;
      if (!hasEligibleMembership) {
        // No active memberships → show gate overlay
        setGateOpen(true);
      } else {
        // Has at least one membership → open composer
        setOpen(true);
      }
    } catch {
      // If anything fails, fail-safe to the gate
      setGateOpen(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-50 bar-translucent bar-no-border border-t border-[var(--border)] pb-[var(--safe-area-bottom)]"
        role="navigation"
        aria-label="Primary"
      >
        <div className="container-app flex h-16 items-center justify-around">
          {/* Home */}
          <button
            type="button"
            onClick={() => navigateHomeTab("latest")}
            aria-label="Home"
            className={`btn-halo-icon inline-flex flex-col items-center justify-center gap-1 text-xs rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)] ${
              isHomeTab ? activeClr : baseClr
            }`}
            aria-current={isHomeTab ? "page" : undefined}
          >
            <span className="icon-halo">
              <House className="h-6 w-6" aria-hidden />
            </span>
            <span>Home</span>
          </button>

          {/* Post */}
          <button
            type="button"
            onClick={handlePostClick}
            disabled={checking}
            className={`btn-halo-icon inline-flex flex-col items-center justify-center gap-1 text-xs rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)] ${baseClr}`}
            aria-label="Post"
          >
            <span className="icon-halo">
              {checking ? (
                <span className="h-6 w-6 inline-block rounded-full border-2 border-[var(--fg-secondary)] border-t-transparent animate-spin" />
              ) : (
                <Plus className="h-6 w-6" aria-hidden />
              )}
            </span>
            <span>Post</span>
          </button>

          {/* My Hubs */}
          <button
            type="button"
            onClick={() => navigateHomeTab("myhubs")}
            aria-label="My Spaces"
            className={`btn-halo-icon inline-flex flex-col items-center justify-center gap-1 text-xs rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)] ${
              isMyHubsTab ? activeClr : baseClr
            }`}
            aria-current={isMyHubsTab ? "page" : undefined}
          >
            <span className="icon-halo">
              <CircleDot className="h-6 w-6" aria-hidden />
            </span>
            <span>My Spaces</span>
          </button>
        </div>
      </nav>

      {/* Composer Drawer */}
      <DiscussionDrawer
        open={open}
        onClose={() => setOpen(false)}
        hideHeader
        bodyClassName="h-full overflow-hidden p-0"
      >
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex flex-col gap-1 border-b border-[var(--border)] px-4 py-2 shadow-[0_1px_6px_rgba(10,10,10,0.12)]">
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-1.5 w-12 rounded-full bg-[var(--muted)]/30"
                aria-label="Close composer"
              />
            </div>
            <div className="flex items-center justify-between px-1">
              <p className="text-xs uppercase tracking-wide text-[var(--muted)]">Create Post</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full border border-[var(--border)] p-1 text-[var(--muted)] hover:text-[var(--brand)]"
                aria-label="Close composer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-[var(--surface-2)] px-4 py-4">
            {open ? (
              <CreateDiscussionComposer
                mode="post"
                hubSlug={hubSlug || undefined}
                showHubSelect={!inHub}
                onSubmitted={() => setOpen(false)}
                onCancel={() => setOpen(false)}
                variant="inline"
                autoFocus
                focusTrigger={open ? 1 : 0}
              />
            ) : null}
          </div>
        </div>
      </DiscussionDrawer>

      {/* Membership Gate Drawer */}
      <DiscussionDrawer
        open={gateOpen}
        title="You need a space to post"
        onClose={() => setGateOpen(false)}
      >
        <div className="p-2">
          <p className="text-[15px] leading-6 text-[var(--fg-secondary)]">
            To create a post, join an existing space or create your own first.
          </p>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Link
              href="/?t=hubs"
              className="btn"
              onClick={() => setGateOpen(false)}
            >
              Join a Space
            </Link>
            {!portal ? (
              <Link
                href="/new-hub"
                className="btn"
                onClick={() => setGateOpen(false)}
              >
                Create a Space
              </Link>
            ) : null}
          </div>
        </div>
      </DiscussionDrawer>
    </>
  );
}
